// TODO: (Taiga UI migration) TuiCarousel is deprecated. Migrate to the new variant TuiCarousel from @taiga-ui/core. See https://taiga-ui.dev/components/carousel
import { TuiCarousel } from '@taiga-ui/legacy';
import { DatePipe } from '@angular/common';
import {
	Component,
	computed,
	effect,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ViewActivityDialogComponent } from '@context/frontend/activity';
import { AlertService } from '@context/frontend/alert';
import {
	DialogService,
	NoPropagationDirective,
	RelativeTimePipe,
} from '@context/frontend/common';
import { MediaThumbnailComponent } from '@context/frontend/media';
import { getUaDevice, IsDevicePipe } from '@context/frontend/native/platform';
import { StorageService } from '@context/frontend/storage';
import {
	AcknowledgeDialogComponent,
	DetailMenuDialogComponent,
	DetailMenuDropdownComponent,
} from '@context/frontend/ui/components';
import {
	AuthService,
	AvatarComponent,
	UserNamePipe,
} from '@context/frontend/user';
import { ViewerService } from '@context/frontend/viewer';
import {
	Content,
	DetailMenuItem,
	hasInheritedViewAccess,
	PresetCompressions,
	ViewAccess,
} from '@context/shared/types/common';
import { ContentLog } from '@context/shared/types/log';
import {
	ContentMedia,
	getPreferredThumbnail,
} from '@context/shared/types/media';
import { hasInheritedRolePermission, User } from '@context/shared/types/user';
import { TranslocoModule } from '@jsverse/transloco';
import {
	TuiButton,
	TuiDropdown,
	TuiIcon,
	TuiLoader,
	TuiSizeL,
	TuiSizeS,
} from '@taiga-ui/core';
import { TuiBadge } from '@taiga-ui/kit';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { DocumentReference, getDoc } from 'firebase/firestore';
import { LogFormDialogComponent } from '../../dialogs';
import { LogService } from '../../services';
import { LogThumbnailComponent } from '../log-thumbnail/log-thumbnail.component';

@Component({
	imports: [
		RelativeTimePipe,
		TranslocoModule,
		DatePipe,
		TuiIcon,
		DetailMenuDropdownComponent,
		IsDevicePipe,
		TuiDropdown,
		TuiBadge,
		LogThumbnailComponent,
		AvatarComponent,
		UserNamePipe,
		TuiCarousel,
		TuiLoader,
		TuiButton,
		MediaThumbnailComponent,
		NoPropagationDirective,
	],
	selector: 'ctx-log-card',
	templateUrl: 'log-card.component.html',
	styleUrl: 'log-card.component.scss',
	host: {
		'[attr.device]': 'device',
		'[attr.size]': 'size()',
		'[attr.archived]': 'isArchived()',
	},
})
export class LogCardComponent {
	readonly device = getUaDevice();

	readonly size = input<TuiSizeL | TuiSizeS | 'full'>('l');

	readonly parent = input.required<Content>();

	readonly parentRef = input.required<DocumentReference<Content>>();

	readonly log = input.required<ContentLog>();

	readonly logRef = input.required<DocumentReference<ContentLog>>();

	readonly actionable = input(true);

	readonly access = input<ViewAccess | null>(null);

	readonly update = output<ContentLog | null>();

	readonly dialog = inject(DialogService);
	readonly logService = inject(LogService);
	readonly alert = inject(AlertService);
	readonly authService = inject(AuthService);
	readonly storageService = inject(StorageService);
	readonly route = inject(ActivatedRoute);
	readonly viewer = inject(ViewerService);

	readonly isArchived = computed(() => !!this.log().archivedAt);

	menuOpen = false;
	readonly collapsed = signal(true);

	readonly menuItems = signal<DetailMenuItem[]>([]);

	readonly loading = signal(false);
	readonly author = signal<User | null>(null);
	readonly media = signal<ContentMedia[]>([]);

	readonly thumbnailUrl = signal<string | null>(null);

	// Local state for log since it can be updated
	readonly currentLog = signal<ContentLog | null>(null);

	// Local state for access since it can be computed
	readonly currentAccess = signal<ViewAccess | null>(null);

	readonly defaultMenuItems: DetailMenuItem[] = [
		{
			text: 'action.view-history',
			iconStart: '@tui.history',
			handler: () => this.onViewHistory(),
			access: 'share',
		},
	];

	readonly activeMenuItems: DetailMenuItem[] = [
		{
			iconStart: '@tui.pencil-line',
			iconEnd: '@tui.chevron-right',
			text: 'content.edit-details',
			handler: () => this.onEdit(),
			access: 'edit',
		},
		{
			iconStart: '@tui.archive',
			text: 'log.archive-log',
			handler: () => this.onArchive(),
			access: 'edit',
		},
	];

	readonly archivedMenuItems: DetailMenuItem[] = [
		{
			iconStart: '@tui.archive-restore',
			text: 'content.restore-from-archive',
			handler: () => this.onUnarchive(),
			access: 'edit',
		},
	];

	readonly logDetailMenuDialog = () =>
		this.dialog.open(
			new PolymorpheusComponent(DetailMenuDialogComponent),
			DetailMenuDialogComponent.Options({
				label: 'log.manage-log',
				data: { menuItems: this.menuItems() },
			}),
		);

	readonly logEditDialog = () =>
		this.dialog.open(
			new PolymorpheusComponent(LogFormDialogComponent),
			LogFormDialogComponent.Options({
				label: 'log.edit-log',
				data: {
					log: this.currentLog(),
					logRef: this.logRef(),
					contentRef: this.parentRef(),
				},
			}),
		);

	readonly fileActivityDialog = () =>
		this.dialog.open(
			new PolymorpheusComponent(ViewActivityDialogComponent),
			ViewActivityDialogComponent.Options({
				data: { entityRef: this.logRef() },
			}),
		);

	readonly acknowledgeDialog = (
		label: string,
		description: string,
		acknowledge: string,
	) =>
		this.dialog.open(
			new PolymorpheusComponent(AcknowledgeDialogComponent),
			AcknowledgeDialogComponent.Options({
				label,
				data: {
					description,
					acknowledge,
					translationParams: { log: this.currentLog()!.name },
				},
			}),
		);

	readonly logArchiveDialog = () =>
		this.acknowledgeDialog(
			'log.archive-header',
			'log.archive-description',
			'log.archive-acknowledge',
		);

	readonly logUnarchiveDialog = () =>
		this.acknowledgeDialog(
			'log.unarchive-header',
			'log.unarchive-description',
			'log.unarchive-acknowledge',
		);

	readonly logDeleteDialog = () =>
		this.acknowledgeDialog(
			'log.delete-header',
			'log.delete-description',
			'log.delete-acknowledge',
		);

	constructor() {
		effect(() => {
			this.currentLog.set(this.log());
			this.initialize();
		});
	}

	initialize() {
		this.currentAccess.set(this.getAccess());
		this.determineThumbnail();

		if (this.currentAccess() !== 'view') {
			this.setupMenuItems();
		}
	}

	getAccess(): ViewAccess {
		const user = this.authService.user;
		if (!user) return 'view';

		const log = this.currentLog()!;
		if (user.id === log.createdBy.id) return 'edit';

		return this.access() ?? 'view';
	}

	determineThumbnail() {
		const thumbnail = getPreferredThumbnail(this.currentLog()!.thumbnails, {
			resolution: PresetCompressions.thumbnail.maxWidthOrHeight,
		});
		if (!thumbnail) return;

		this.storageService
			.getDownloadUrl(
				this.storageService.getReference(thumbnail.storagePath),
			)
			.then((url) => {
				this.thumbnailUrl.set(url);
			});
	}

	setupMenuItems() {
		if (!this.currentAccess) {
			this.menuItems.set([]);
			return;
		}

		const items = [];
		if (this.isArchived()) {
			items.push(...this.archivedMenuItems);

			// we can only delete the log if the user is an admin or greater
			const currentUser = this.authService.user as User;
			if (hasInheritedRolePermission(currentUser.role, 'admin')) {
				items.push({
					iconStart: '@tui.trash',
					text: 'log.delete-log',
					handler: () => this.onDelete(),
				});
			}
		} else {
			items.push(...this.activeMenuItems);
		}

		const access = this.currentAccess();
		items.push(...this.defaultMenuItems);
		this.menuItems.set(
			items.filter(
				(i) => !i.access || hasInheritedViewAccess(access!, i.access),
			),
		);
	}

	readonly onLoadAuthor = () =>
		getDoc(this.currentLog()!.createdBy as DocumentReference<User>).then(
			(ref) => {
				this.author.set(ref.data() as User);
			},
		);

	readonly onLoadMedia = () =>
		Promise.all([
			...this.currentLog()!.media.map((m) =>
				getDoc(m as DocumentReference<ContentMedia>).then(
					(doc) => doc.data() as ContentMedia,
				),
			),
		]).then((media) => {
			this.media.set(media);
		});

	onToggleCollapsed() {
		this.collapsed.update((value) => !value);

		if (
			!this.collapsed() &&
			(!this.author() ||
				this.media()!.length !== this.currentLog()!.media.length)
		) {
			this.loading.set(true);
			Promise.all([this.onLoadAuthor(), this.onLoadMedia()]).finally(
				() => {
					this.loading.set(false);
				},
			);
		}
	}

	readonly onMenuOpen = () => this.logDetailMenuDialog().subscribe();

	readonly onViewHistory = () => this.fileActivityDialog().subscribe();

	readonly onView = (media: ContentMedia) => this.viewer.preview(media);

	readonly onEdit = () =>
		this.logEditDialog().subscribe({
			next: (data) => {
				if (!data) return;

				// Create a new object reference to ensure signal updates properly
				this.currentLog.set({
					...this.currentLog()!,
					...(data as Partial<ContentLog>),
				});

				this.initialize();

				// Emit update event for parent components
				this.update.emit(this.currentLog()!);
			},
		});

	readonly onArchive = () =>
		this.logArchiveDialog().subscribe({
			next: (data) => {
				if (!data) return;
				this.logService
					.archive(
						this.parentRef(),
						this.logRef(),
						this.currentLog()!,
					)
					.then((data) => {
						this.currentLog.update((log) =>
							Object.assign({}, log, data),
						);
						this.initialize();

						this.alert.open('generic-success').subscribe();
						this.update.emit(data as ContentLog);
					})
					.catch((error) => {
						console.error(
							'There was an issue deleting the file',
							error,
						);
						this.alert.open('generic-error').subscribe();
						throw new Error(
							'There was an issue deleting on the file',
						);
					});
			},
		});

	readonly onUnarchive = () =>
		this.logUnarchiveDialog().subscribe({
			next: (data) => {
				if (data) {
					this.logService
						.unarchive(
							this.parentRef(),
							this.logRef(),
							this.currentLog()!,
						)
						.then((data) => {
							this.currentLog.update((log) =>
								Object.assign({}, log, data),
							);
							this.initialize();

							this.alert.open('generic-success').subscribe();
							this.update.emit(data as ContentLog);
						})
						.catch((error) => {
							console.error(
								'There was an issue deleting the file',
								error,
							);
							this.alert.open('generic-error').subscribe();
							throw new Error(
								'There was an issue deleting on the file',
							);
						});
				}
			},
		});

	readonly onDelete = () =>
		this.logDeleteDialog().subscribe({
			next: (data) => {
				if (data) {
					this.logService
						.delete(
							this.parent(),
							this.logRef(),
							this.currentLog()!,
						)
						.then(() => {
							this.alert.open('generic-success').subscribe();
							this.update.emit(null);
						})
						.catch((error) => {
							console.error(
								'There was an issue deleting the file',
								error,
							);
							this.alert.open('generic-error').subscribe();
							throw new Error(
								'There was an issue deleting on the file',
							);
						});
				}
			},
		});
}
